import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Users, 
  Award, 
  TrendingUp, 
  Activity,
  MapPin,
  PlayCircle,
  Eye,
  Brain,
  Package,
  Calendar,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { useAdminDashboard, useActivitySummary } from '../../hooks';
import { Card, LoadingSpinner, Badge } from '../../components/common';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  PieChart, 
  Pie, 
  Cell 
} from 'recharts';

import ResponsivePieLabel from '@/components/admin/ResponsivePieLabel';

import { Download } from 'lucide-react';
import { exportChartAsImage } from '@/utils/exportChartAsImage';

import QuickActions from '@/components/admin/QuickActions';



const AdminDashboard: React.FC = () => {
  // Datos principales del dashboard (usuarios, módulos, etc.)
  const { data: dashboardData, isPending } = useAdminDashboard();

  // Nuevo hook: resumen global de actividades completadas
  const { data: activitySummary, isLoading: isSummaryLoading } = useActivitySummary();

  // ===========================
  // Logs de verificación
  // ===========================
  // console.log("📊 dashboardData:", dashboardData);
  // console.log("🎯 activitySummary (from /activities/summary):", activitySummary);

  if (isPending) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const stats = dashboardData || {
    totalUsers: 0,
    activeUsers: 0,
    completedTutorials: 0,
    totalCertificates: 0,
    activitiesByZone: [],
    popularModules: []
  };

  
  // ===========================
  // Datos para gráficos
  // ===========================
  const zoneData = stats.activitiesByZone.map((zone: any) => ({
    name: zone.zone || 'Sin especificar',
    users: Number(zone.count)
  }));

  const moduleData = stats.popularModules.map((module: any) => ({
    name: module.categoryName,
    visits: Number(module.visits)
  }));

  // ===========================
  // Paleta Brinsa
  // ===========================
  const BRINSA_HEX = {
    azul: '#001689',
    celeste: '#35C1D7',
    verde: '#00B93D',
    morado: '#B32BA6',
    amarillo: '#F8A703'
  };

  const PIE_COLORS = [
    BRINSA_HEX.azul,
    BRINSA_HEX.verde,
    BRINSA_HEX.celeste,
    BRINSA_HEX.morado,
    BRINSA_HEX.amarillo
  ];

  const tones = {
    blue:   { chipBg: 'bg-brinsa-azul/10',     icon: 'text-brinsa-azul' },
    green:  { chipBg: 'bg-brinsa-verde/10',    icon: 'text-brinsa-verde' },
    yellow: { chipBg: 'bg-brinsa-amarillo/10', icon: 'text-brinsa-amarillo' },
    purple: { chipBg: 'bg-brinsa-morado/10',   icon: 'text-brinsa-morado' },
  } as const;

  // ===========================
  // Tarjetas KPI
  // ===========================
  const kpiCards = [
    {
      title: 'Total Usuarios',
      value: stats.totalUsers,
      icon: Users,
      color: 'blue',
    },
    {
      title: 'Usuarios Activos',
      value: stats.activeUsers,
      icon: Activity,
      color: 'green',
    },
    {
      title: 'Tutoriales Completados',
      value: stats.completedTutorials,
      icon: PlayCircle,
      color: 'yellow',
    },
    {
      title: 'Certificados Emitidos',
      value: stats.totalCertificates,
      icon: Award,
      color: 'purple',
    },
  ];

  // ===========================
  // Acciones rápidas (admin)
  // ===========================
  const quickActions = [
    {
      title: 'Gestionar Usuarios',
      description: 'Crear, editar y administrar usuarios',
      icon: Users,
      href: '/admin/users',
      color: 'blue'
    },
    {
      title: 'Crear Notificación',
      description: 'Enviar mensajes a usuarios',
      icon: Activity,
      href: '/admin/notifications',
      color: 'green'
    },
    {
      title: 'Ver Reportes',
      description: 'Análisis detallado de la plataforma',
      icon: BarChart3,
      href: '/admin/reports',
      color: 'purple'
    },
    {
      title: 'Gestionar Contenido',
      description: 'Administrar productos y categorías',
      icon: Package,
      href: '/admin/content',
      color: 'yellow'
    }
  ];

  // ===========================
  // Render principal
  // ===========================
  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-brinsa-azul mb-2">
              Panel de Administración
            </h1>
            <p className="text-lg text-brinsa-azulOscuro/80">
              Vista general a modo de resumen de los datos de la plataforma Universidad BrinsAprende
            </p>
          </div>

          <div className="flex items-center space-x-2 text-sm text-brinsa-azulOscuro/80">
            <Calendar className="w-4 h-4" />
            <span>Actualizado: {new Date().toLocaleDateString('es-ES')}</span>
          </div>
        </div>
      </motion.div>

      {/* KPI Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        {kpiCards.map((kpi, index) => {
          const tone = tones[kpi.color as keyof typeof tones] || tones.blue;
          return (
            <motion.div
              key={kpi.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-brinsa-azulOscuro/80 mb-1">
                      {kpi.title}
                    </p>
                    <p className="text-3xl font-bold text-brinsa-azul">
                      {kpi.value?.toLocaleString?.() ?? 0}
                    </p>
                  </div>
                  <div
                    className={`w-12 h-12 ${tone.chipBg} rounded-2xl grid place-items-center`}
                  >
                    <kpi.icon className={`w-6 h-6 ${tone.icon}`} />
                  </div>
                </div>
              </Card>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Usuarios por zona */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Card title="Usuarios por Zona Geográfica">
            <div className="flex justify-end mb-2">
              <button
                onClick={() => exportChartAsImage("chart-zonas", "usuarios_por_zona.png")}
                className="flex items-center gap-1 text-sm text-brinsa-azulOscuro/80 hover:text-brinsa-azul transition-colors"
              >
                <Download className="w-4 h-4" /> Exportar Imagen
              </button>
            </div>

            <div id="chart-zonas" className="h-80 bg-white p-2 rounded-md">
              {zoneData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={zoneData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="name" tick={{ fill: "#334155" }} />
                    <YAxis tick={{ fill: "#334155" }} />
                    <Tooltip />
                    <Bar dataKey="users" fill={BRINSA_HEX.azul} radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-brinsa-azulOscuro/70">
                  <div className="text-center">
                    <MapPin className="w-12 h-12 mx-auto mb-4 text-brinsa-azul/40" />
                    <p>No hay datos de zona geográfica</p>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </motion.div>

        {/* Módulos más populares */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Card title="Módulos Más Visitados">
            <div className="flex justify-end mb-2">
              <button
                onClick={() => exportChartAsImage("chart-modulos", "modulos_mas_visitados.png")}
                className="flex items-center gap-1 text-sm text-brinsa-azulOscuro/80 hover:text-brinsa-azul transition-colors"
              >
                <Download className="w-4 h-4" /> Exportar Imagen
              </button>
            </div>

            <div id="chart-modulos" className="h-80 bg-white p-2 rounded-md">
              {moduleData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    {/* <Pie
                      data={moduleData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={86}
                      dataKey="visits"
                    >
                      {moduleData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie> */}
                    <Pie
                      data={moduleData}
                      cx="50%"
                      cy="50%"
                      outerRadius={86}
                      dataKey="visits"
                      labelLine={false}
                      label={(props) => (
                        <ResponsivePieLabel
                          {...props}
                          chartWidth={window.innerWidth}
                        />
                      )}
                    >
                      {moduleData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-brinsa-azulOscuro/70">
                  <div className="text-center">
                    <BarChart3 className="w-12 h-12 mx-auto mb-4 text-brinsa-azul/40" />
                    <p>No hay datos de módulos</p>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Resumen de Actividad */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.5 }}
      >
        <Card title="Resumen de Actividad">
          {isSummaryLoading ? (
            <div className="flex justify-center py-6">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-brinsa-azul/10 rounded-2xl grid place-items-center mx-auto mb-3">
                    <PlayCircle className="w-6 h-6 text-brinsa-azul" />
                  </div>
                  <div className="text-2xl font-bold text-brinsa-azul mb-1">
                    {activitySummary?.video ?? 0}
                  </div>
                  <div className="text-sm text-brinsa-azulOscuro/80">Videos completados</div>
                </div>

                <div className="text-center">
                  <div className="w-12 h-12 bg-brinsa-morado/10 rounded-2xl grid place-items-center mx-auto mb-3">
                    <Eye className="w-6 h-6 text-brinsa-morado" />
                  </div>
                  <div className="text-2xl font-bold text-brinsa-azul mb-1">
                    {activitySummary?.ar ?? 0}
                  </div>
                  <div className="text-sm text-brinsa-azulOscuro/80">Sesiones AR</div>
                </div>

                <div className="text-center">
                  <div className="w-12 h-12 bg-brinsa-verde/10 rounded-2xl grid place-items-center mx-auto mb-3">
                    <Brain className="w-6 h-6 text-brinsa-verde" />
                  </div>
                  <div className="text-2xl font-bold text-brinsa-azul mb-1">
                    {activitySummary?.test ?? 0}
                  </div>
                  <div className="text-sm text-brinsa-azulOscuro/80">Tests completados</div>
                </div>

                <div className="text-center">
                  <div className="w-12 h-12 bg-brinsa-amarillo/10 rounded-2xl grid place-items-center mx-auto mb-3">
                    <Package className="w-6 h-6 text-brinsa-amarillo" />
                  </div>
                  <div className="text-2xl font-bold text-brinsa-azul mb-1">
                    {activitySummary?.portfolio ?? 0}
                  </div>
                  <div className="text-sm text-brinsa-azulOscuro/80">Portafolios completados</div>
                </div>
              </div>

             {/* 🔍 Debug visual: mostrar JSON completo del endpoint
              <pre className="mt-6 text-xs bg-slate-50 p-3 rounded-md text-left text-slate-600 overflow-x-auto">
                {JSON.stringify(activitySummary, null, 2)}
              </pre>  */}
            </>
          )}
        </Card>
      </motion.div>

      {/* Acciones rápidas */}
      <QuickActions />
    </div>
  );
};

export default AdminDashboard;
